import React, { useCallback, useRef, useState } from "react";

import { formatDateOnly, formatDateTime } from "../../../../utils/datetime";
import { addressLines } from "./overviewSelectors";
import {
  CardCta,
  CardDivider,
  CopyIcon,
  MapPinIcon,
  MetaRow,
  OverviewCard,
  StatusPill,
} from "./overviewUi";

/**
 * "Adresse & Übersicht" — the current residential address plus the handful of
 * facts that identify this tenant.
 *
 * THE ADDRESS COMES FROM THE ADDRESS BOOK, NOT THE FLAT MIRROR. `tenant.street/
 * postal_code/city/country` are a server-side mirror of the current address kept
 * for PDF/Invoice/Contract; reading them here would show a copy and could not
 * render the "Aktuell" badge or the period. So the card reads the row the
 * address contract itself marks `is_current`, and says so plainly when there is
 * none rather than quietly falling back to the mirror.
 *
 * The full address book — current / former / other addresses, "Adresswechsel
 * erfassen", add, edit, remove — is NOT removed by this redesign. It moved
 * behind the footer action, which the parent expands directly below this card.
 *
 * A FAILED READ IS NOT AN EMPTY BOOK. `addressesError` exists because those two
 * used to arrive here as the same thing — an empty array — and the card then
 * stated, with full confidence, that no address had been recorded. It now says
 * what actually happened. When a refresh fails while an address is already on
 * screen, both are shown: the address that was last read, and the note that the
 * latest read did not come back.
 *
 * Bexio is deliberately absent: the connection is one row per ORGANISATION
 * (`bexio_connections`, unique per org), so a "Verbunden" line here would read
 * identically for every tenant and state nothing about this one.
 */
export default function AddressOverviewCard({
  tenant,
  address,
  addressesLoading = false,
  addressesError = null,
  tenancyCount,
  lastActivityAt,
  historyOpen = false,
  onToggleHistory,
}) {
  const [copied, setCopied] = useState(false);
  const copyTimer = useRef(null);

  const tenantId = tenant?.id ? String(tenant.id) : "";

  const handleCopy = useCallback(() => {
    if (!tenantId) return;
    const done = () => {
      setCopied(true);
      if (copyTimer.current) clearTimeout(copyTimer.current);
      copyTimer.current = setTimeout(() => setCopied(false), 1500);
    };
    try {
      const clip = navigator?.clipboard;
      if (clip && typeof clip.writeText === "function") {
        clip.writeText(tenantId).then(done, () => {});
        return;
      }
    } catch {
      /* clipboard unavailable — the id stays selectable by hand */
    }
  }, [tenantId]);

  const lines = addressLines(address);

  return (
    <OverviewCard
      title="Adresse & Übersicht"
      icon={<MapPinIcon />}
      accent="violet"
      testId="overview-address-card"
      footer={
        onToggleHistory ? (
          <CardCta onClick={onToggleHistory} testId="overview-address-history-cta">
            {historyOpen ? "Adresshistorie ausblenden" : "Adresshistorie anzeigen"}
          </CardCta>
        ) : null
      }
    >
      <div className="flex items-center justify-between gap-3">
        <span className="text-[12.5px] font-medium text-muted-foreground">
          Aktuelle Adresse
        </span>
        {address ? (
          <StatusPill tone="emerald" testId="overview-address-current-badge">
            Aktuell
          </StatusPill>
        ) : null}
      </div>

      {address ? (
        <div
          className="mt-2 space-y-0.5 text-[13.5px] text-card-foreground"
          data-testid="overview-address-lines"
        >
          {lines.map((line) => (
            <p key={line} className="m-0">
              {line}
            </p>
          ))}
          {address.valid_from ? (
            <p className="m-0 pt-1 text-[12px] text-muted-foreground">
              gültig seit {formatDateOnly(address.valid_from)}
            </p>
          ) : null}
        </div>
      ) : addressesError ? null : (
        <p
          className="m-0 mt-2 text-[13px] leading-snug text-muted-foreground"
          data-testid="overview-address-empty"
        >
          {addressesLoading
            ? "Adresse wird geladen …"
            : "Keine Wohnadresse im Adressbuch erfasst."}
        </p>
      )}

      {addressesError ? (
        <p
          className="m-0 mt-2 text-[13px] leading-snug text-destructive"
          data-testid="overview-address-error"
        >
          {addressesError}
        </p>
      ) : null}

      <CardDivider />

      <div className="divide-y divide-border">
        <MetaRow
          label="Mieter-ID"
          value={tenantId || null}
          mono
          action={
            tenantId ? (
              <button
                type="button"
                onClick={handleCopy}
                title="Mieter-ID kopieren"
                aria-label="Mieter-ID kopieren"
                data-testid="overview-copy-tenant-id"
                className="inline-flex items-center justify-center rounded-md border border-border p-1 text-muted-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <CopyIcon />
              </button>
            ) : null
          }
        />
        <MetaRow
          label="Mieter seit"
          value={tenant?.created_at ? formatDateOnly(tenant.created_at) : null}
        />
        <MetaRow
          label="Mietverhältnisse"
          value={
            Number.isFinite(Number(tenancyCount)) ? String(tenancyCount) : null
          }
        />
        <MetaRow
          label="Letzte Aktivität"
          value={lastActivityAt ? formatDateTime(lastActivityAt) : null}
        />
      </div>

      {copied ? (
        <p className="m-0 pt-2 text-[11.5px] text-muted-foreground" role="status">
          Mieter-ID kopiert.
        </p>
      ) : null}
    </OverviewCard>
  );
}
